// FE Polish Phase 2 — Sentry browser SDK initialization.
//
// Loaded synchronously from src/main.js BEFORE either the Vue or React app
// boots, so unhandled errors from either runtime are captured.
//
// Configuration (env-driven, all VITE_-prefixed so Vite exposes them):
//   VITE_SENTRY_DSN          — project DSN. If unset, init is a no-op (dev).
//   VITE_SENTRY_ENVIRONMENT  — 'staging' | 'prod' | 'local' (default: mode)
//   VITE_SENTRY_TRACES_SAMPLE_RATE — float 0..1 (default 0.1)
//   VITE_RELEASE             — release tag (set by CI, matches Sentry release
//                              marker created in release-prod.yml).
//
// We intentionally avoid:
//   - Session Replay (extra payload, separate billing tier)
//   - BrowserProfilingIntegration (still beta, browser-only)
// Both can be added in a later phase if there's demand.

import * as Sentry from '@sentry/react'
import { redactBreadcrumb, redactEvent } from './redact'

let initialized = false

export function initSentry() {
  if (initialized) return
  initialized = true

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) {
    // No DSN configured — common locally. Quiet no-op so dev doesn't fight
    // network requests to a non-existent ingest endpoint.
    return
  }

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    (import.meta.env.MODE as string) ||
    'local'

  const tracesSampleRate = Number.parseFloat(
    (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ||
      (environment === 'prod' ? '0.1' : '0.2'),
  )

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_RELEASE as string | undefined,
    // Performance monitoring — modest sample rate so traces don't blow the quota.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.1,
    // Avoid sending PII (emails, IPs). Backend audit log is the source of
    // truth for who-did-what; Sentry should only know what broke.
    sendDefaultPii: false,
    // Phase 8 — PII redaction for any breadcrumb or event that still leaks
    // through default capture (e.g., emails in URLs, JWTs in query strings,
    // Authorization headers in fetch breadcrumbs).
    beforeBreadcrumb: redactBreadcrumb,
    beforeSend: redactEvent,
    // Filter noisy errors that are not actionable.
    ignoreErrors: [
      // Network errors during dev when backend is down — handled in-app.
      'Network Error',
      // Browser extensions injecting scripts.
      'top.GLOBALS',
      // Cross-origin script errors with no detail.
      'Script error.',
      // ResizeObserver loop limit — benign, browser-internal.
      /ResizeObserver loop/i,
    ],
  })
}

// Re-export Sentry surface for callers (ErrorBoundary, captureException, etc.)
export { Sentry }
